//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useState, useEffect } from 'react';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Modal,
  Paper,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar
} from '@mui/material';


import MuiAlert from '@mui/material/Alert';

import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';

import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// === API ===
import { prokeyApi } from 'api/prokey';
import { createStatsScheduleTask } from 'api/prokey/prokey.websocket';

// === UTILS ===
import { regex_start_of_word, spaceBeforeWord } from 'components/utils';

// === COMPONENTS ===
import { UserSelect } from 'components/UserSelect/index';

//-----------------------------------------------------------------
function Alert(props) {
  //-----------------------------------------------------------------
  return <MuiAlert elevation={6} variant='filled' {...props} />;
}

//#endregion

//-----------------------------------------------------------------
export function TaskModal( props ) {
  //-----------------------------------------------------------------

  //#region [VALUES]
  
  const theme = useTheme();

  const [ report_names, setReportNames ] = useState( [] );
  const [ report_name , setReportName  ] = useState( '' );

  const [ date_start , setDateStart ] = useState( null );
  const [ time_start , setTimeStart ] = useState( null );
  const [ frequency  , setFrequency ] = useState(  ''  );
  const [ user       , setUser      ] = useState(  ''  );
  const [ loading    , setLoading   ] = useState( false );
  const [ success    , setSuccess   ] = useState( false );

  const [ message, setMessage          ] = useState('');
  const [ show_message, setShowMessage ] = useState( false );

  const _frequencies = [
    'Daily'    ,
    'Weekly'   ,
    'Monthly'  ,
    'One Time' ,
  ]

  //#endregion

  //#region [STYLES]

  const _styles = {
    modal: {
    },
    container: {
      position       : 'absolute',
      top            : '12rem',
      right          : '3rem',
      // width          : '50%',
      // bgcolor        : 'black',
      boxShadow      : 24,
      padding        : '1rem',
      display        : 'block' ,
      alignText      : 'center',
      justifyContent : 'center',
      alignItems     : 'middle',
      alignContent   : 'middle',
    },
    reports_label: {

    },
    reports_select: {
      display: 'block',
      marginBottom: '1rem',
      height: '4rem',
      width: '18rem',
      clear: 'both',
    },
    date_start: {
      display: 'block',
      marginBottom: '1rem',
      width: '18rem',
      clear: 'both',
    },
    time_start: {
      display: 'block',
      marginLeft  : '1rem',
      marginBottom: '1rem',
      width: '18rem',
      clear: 'both',
    },
    select_frequency: {
      display: 'block',
      width: '15rem',
      marginBottom: '1rem',
      width: '18rem',
    },
    button_schedule: {
      marginLeft : '1rem'  ,
      height     : '4rem'  ,
      width      : '16rem' ,
      backgroundColor: success ? theme.palette.success.main : theme.palette.primary.main,
      '&:hover': {
        backgroundColor: success ? theme.palette.success.dark : theme.palette.primary.main.dark,
      },
    },
  }
  // === ATTRIBUTES ===
  const
    MODAL = {
      sx      : _styles.modal,
      open    : props.is_open,
      onClose : props.onClose,
    },
    CONTAINER = {
      sx: _styles.container,
    },
    REPORTS_LABEL = {
      id       : 'reports-label',
      children : [ 'Report Name' ],
    },
    REPORTS_SELECT = {
      id       : 'reports-select',
      labelId  : 'reports-label' ,
      sx       : _styles.reports_select ,
      value    : report_name            ,
      label    : 'Report Name'          ,
      onChange : (event) => setReportName(event.target.value),
      children : report_names.map( (name) => <MenuItem value={name}>{name}</MenuItem> ),
    },
    DATE_START = {
      sx       : _styles.date_start ,
      value    : date_start         ,
      label    : 'Start Date'       ,
      onChange : setDateStart       ,
    },
    TIME_START = {
      sx       : _styles.date_start ,
      value    : time_start         ,
      label    : 'Time'             ,
      onChange : setTimeStart       ,
    },
    FREQUENCY_LABEL = {
      id       : 'frequency-label',
      children : [ 'Frequency' ],
    },
    FREQUENCY_SELECT = {
      id       : 'frequency-select',
      labelId  : 'frequency-label' ,
      sx       : _styles.select_frequency ,
      value    : frequency                ,
      label    : 'Frequency'              ,
      onChange : (event) => setFrequency(event.target.value),
      children : _frequencies.map( (item) => <MenuItem value={item}>{item}</MenuItem> ),
    },
    BUTTON_SCHEDULE = {
      sx       : _styles.button_schedule ,
      variant  : 'contained'           ,
      color    : 'primary'             ,
      disabled : loading               ,
      endIcon  : <ScheduleSendIcon />  ,
      onClick  : handleSchedule        ,
      children : ['Schedule']          ,
    },
    MESSAGE = { 
      open             : show_message,
      autoHideDuration : 3000,
      onClose          : handleMessageClose,
      anchorOrigin     : { vertical:'top', horizontal:'center' },
      children:(
        <div>
          <Alert onClose={handleMessageClose} severity='info'>
            {message}
          </Alert>
        </div>
      ),
    };


  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    async function getReportNames() {
      //-----------------------------------------------------------------
      try {
        const response = await prokeyApi.getReportNames();
        if( !response.data ) return;

        const names = response.data.map( x => {
          return x
            .replace('pk_stats_', '')
            .replace( regex_start_of_word, spaceBeforeWord );
        } );
        setReportNames( names );
      } catch(error) {
        console.warn(error);
      }
    }

    getReportNames();
  }, [] );

  //#endregion

  //#region [MODIFY]

  //-----------------------------------------------------------------
  function resetData() {
    //-----------------------------------------------------------------
    setReportName('');
    setDateStart(null);
    setTimeStart(null);
    setFrequency('');
  };

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleUserChange(selectedUserIds) {
    //-----------------------------------------------------------------
    setUser(selectedUserIds);
  };

  //-----------------------------------------------------------------
  function handleSchedule() {
    //-----------------------------------------------------------------
    createStatsScheduleTask( report_name.replace(/ /g, ''), date_start, time_start.format('HH:mm:ss'), frequency, user );
    resetData();
    setMessage('Report has been scheduled');
    setShowMessage(true);
  }

  //-----------------------------------------------------------------
  function handleMessageClose(event, reason) {
    //-----------------------------------------------------------------
    if (reason === 'clickaway') {
      return;
    }
    setShowMessage(false);
  };

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
    <>
      <Modal { ... MODAL }>
        <Paper { ...CONTAINER }>
          <FormControl>
            <InputLabel { ...REPORTS_LABEL  } />
            <Select     { ...REPORTS_SELECT } />
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker { ...DATE_START } />
            <TimePicker { ...TIME_START } />
          </LocalizationProvider>
          <FormControl>
            <InputLabel { ...FREQUENCY_LABEL  } />
            <Select     { ...FREQUENCY_SELECT } />
          </FormControl>
          <UserSelect label='Recipients' onChange={handleUserChange} />
          <Button { ...BUTTON_SCHEDULE } />
        </Paper>
      </Modal>
      <Snackbar {...MESSAGE} />
    </>
    );
  }

  //#endregion

  return display();
}
